import type { UseQueryOptionsType } from './UseQueryOptionsType';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { KEYS, repository } from '@repositories/Repository';
import type ISubscriptionConnectionRepository from '@repositories/subscriptionConnectionRepository/ISubscriptionConnectionRepository';
import type {
  Filter,
  SubscriptionConnectionConnectDTO,
  SubscriptionConnectionCreateDTO,
  SubscriptionConnectionDTO,
  SubscriptionConnectionUpdateDTO,
} from '@repositories/subscriptionConnectionRepository/subscriptionConnection.types';
import type { Page } from '@type/Page';
import { SubscriptionConnectionModel } from '@repositories/subscriptionConnectionRepository/models/SubscriptionConnectionModel';
import { sbQueryKey } from './useSubscription';

const sbConRepo = repository.get<ISubscriptionConnectionRepository>(KEYS.SUBSCRIPTION_CONNECTION_REPOSITORY);
export const sbConQueryKey = {
  all: ['subscriptionConnection'] as const,
  lists: () => [...sbConQueryKey.all, 'list'] as const,
  list: (filter?: Filter) => [...sbConQueryKey.lists(), filter] as const,
  details: () => [...sbConQueryKey.all, 'detail'] as const,
  detail: (subscriptionId: string) => [...sbConQueryKey.details(), subscriptionId] as const,
  oauth: (subscriptionId: string) => ['oauth', subscriptionId] as const,

  connect: (subscriptionId: string) => [...sbConQueryKey.all, 'connect', subscriptionId] as const,
};

export const useGetSubscriptionConnections = (
  queries?: Filter,
  options?: Omit<UseQueryOptions<Page<SubscriptionConnectionDTO>, AxiosError, Page<SubscriptionConnectionModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectConnections = useCallback(
    (data: Page<SubscriptionConnectionDTO>): Page<SubscriptionConnectionModel> => ({
      ...data,
      content: data.content.map(dto => new SubscriptionConnectionModel(dto)),
    }),
    [],
  );
  return useQuery<Page<SubscriptionConnectionDTO>, AxiosError, Page<SubscriptionConnectionModel>>({
    queryKey: sbConQueryKey.list(queries),
    queryFn: () => sbConRepo.getList(queries),
    select: selectConnections,
    ...options,
  });
};

export const useGetSubscriptionConnection = (
  subscriptionId: string,
  options?: Omit<UseQueryOptions<SubscriptionConnectionDTO, AxiosError, SubscriptionConnectionModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectConnection = useCallback(
    (data: SubscriptionConnectionDTO) => new SubscriptionConnectionModel(data),
    [],
  );
  return useQuery<SubscriptionConnectionDTO, AxiosError, SubscriptionConnectionModel>({
    queryKey: sbConQueryKey.detail(subscriptionId),
    queryFn: () => sbConRepo.getOne(subscriptionId),
    select: selectConnection,
    ...options,
  });
};

export const useAddSubscriptionConnection = () => {
  const queryClient = useQueryClient();

  const result = useMutation(
    async (rqData: SubscriptionConnectionCreateDTO) => {
      const result = await sbConRepo.create(rqData.subscriptionId, rqData);
      return new SubscriptionConnectionModel(result);
    },
    {
      onSuccess: data => {
        queryClient.invalidateQueries({ queryKey: sbConQueryKey.all });
        queryClient.invalidateQueries({ queryKey: sbQueryKey.list() });
        queryClient.invalidateQueries({
          queryKey: sbQueryKey.detail(data.subscription.id),
        });
      },
    },
  );

  return result;
};

export const useDeleteSubscriptionConnection = (subscriptionId: string) => {
  const queryClient = useQueryClient();

  const result = useMutation(
    async () => {
      const result = await sbConRepo.delete(subscriptionId);
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: sbConQueryKey.lists() });
        queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
        queryClient.removeQueries({
          queryKey: sbConQueryKey.detail(subscriptionId),
        });
        queryClient.removeQueries({
          queryKey: sbQueryKey.detail(subscriptionId),
        });
      },
    },
  );

  return result;
};

export const useUpdateSubscriptionConnection = (subscriptionId: string) => {
  const queryClient = useQueryClient();

  const result = useMutation(
    async (rqData: SubscriptionConnectionUpdateDTO) => {
      const result = await sbConRepo.update(rqData.subscriptionId, rqData);
      return new SubscriptionConnectionModel(result);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: sbConQueryKey.detail(subscriptionId),
        });
      },
    },
  );

  return result;
};

export const useConnect = (subscriptionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: subscriptionId ? sbConQueryKey.connect(subscriptionId) : undefined,
    mutationFn: async (rqData: SubscriptionConnectionConnectDTO) => {
      const result = await sbConRepo.connect(rqData.subscriptionId ?? '', rqData);
      return new SubscriptionConnectionModel(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sbConQueryKey.all });
      queryClient.invalidateQueries({ queryKey: sbQueryKey.all });
    },
  });
};
export const useConnectIsMutating = (subscriptionId: string) => useIsMutating(sbConQueryKey.connect(subscriptionId));

export const useDisconnect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      const result = await sbConRepo.disconnect(subscriptionId);
      return new SubscriptionConnectionModel(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sbConQueryKey.all });
      queryClient.invalidateQueries({ queryKey: sbQueryKey.all });
    },
  });
};

export const useConnectWithOauth = (subscriptionId: string, options?: UseQueryOptionsType<string>) => {
  const result = useQuery<string, AxiosError>(
    sbConQueryKey.oauth(subscriptionId),
    async () => {
      const result = await sbConRepo.connectWithOauth(subscriptionId);
      return result;
    },
    options,
  );

  return result;
};
